import { getChallengeExperimentEventProperties } from './challengePageExperiment';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getWebsiteThemeAttribution } from '../../experiments/websiteTheme';
import { ChevronDown } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { Helmet } from 'react-helmet-async';
import PrerenderReady from '../../components/PrerenderReady';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { buildAuthOverlayRouteState } from '../../utils/authOverlayNavigation';
import useUser from '../../hooks/useUser';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import { readPublicChallengeCatalog } from '../../utils/publicChallengeCatalog';
import { pushEvent, trackLeadSubmit } from '../../utils/analytics';
import Reveal from '../../components/course/Reveal';
import PromoCountdown from '../../components/course/PromoCountdown';
import ScrollProgress from '../../components/course/ScrollProgress';
import CourseLegalDisclaimer from '../../components/course/CourseLegalDisclaimer';
import { ROB_LINKEDIN_URL } from '../../components/course/courseContent';
import {
  isEducationWorkbenchVariant,
  preserveEducationPreviewParams,
  useEducationDesignVariant,
} from '../education/EducationDesignExperiment';
import { SmsConsentCheckbox, buildSmsConsentPayload } from '../../components/SmsConsent';
import PhoneInput from '../../components/PhoneInput/PhoneInput';
import { sanitizeInternalDestination } from '../../utils/postSignupJourney';
import { getUtmParamsForApi } from '../../utils/utmTracker';
import {
  buildChallengeCalendarLinks,
  normalizeChallengeSlug,
  AI_TRADING_CHALLENGE_NAME,
  canonicalChallengeRunName,
  canonicalChallengeSessionTitle,
  formatChallengeDate,
  formatChallengeDateTime,
  pickKickoffAndDemo,
  isChallengeReplayAvailable,
  splitDisplayName,
  selectChallengeRun,
  selectCurrentChallengeRun,
  youtubeLiveUrl,
} from './challengePageUtils';
import '../courses/CoursePage.css';
import './ChallengePage.css';
import './ChallengePageControlRoom.css';
import ChallengePageFormFirst, { FORM_FIRST_CTA } from './ChallengePageFormFirst';
import ChallengePageClarityHome from './ChallengePageClarityHome';
import ChallengeFormQuestions from './ChallengeFormQuestions';
import { getBrowserTimeZone } from '../../utils/browserTimeZone';
import { useHideSiteHeaderMenuLinks } from '../../components/siteHeaderOptions';
import { useLeftWithoutBooking } from './leftWithoutBooking';
import {
  CHALLENGE_TRIMMED_COUNTDOWN_LABEL,
  CHALLENGE_TRIMMED_CTA,
  CHALLENGE_TRIMMED_HOST_CREDENTIALS,
  CHALLENGE_TRIMMED_REASSURE,
  useChallengeTrimmedCopy,
} from './challengeTrimmedCopy';
import {
  getChallengeGoogleAdsConsent,
  getChallengeLeadSubmissionId,
  CHALLENGE_QUESTION_VERSION,
  CHALLENGE_LEAD_VALUE_MODEL_VERSION,
  challengeQualifyingLeadValue,
  sendChallengeQualifiedLeadConversion,
  sendChallengeGoodFitLeadConversion,
  CHALLENGE_GOOD_FIT_RULE_VERSION,
  isChallengeGoodFit,
  wasChallengeQualifiedLeadDispatched,
  sendChallengeSeatLead,
  trackChallengeQualifyingAnswered,
} from './challengeQualifyingLead';
const { challengeEventStructuredData } = require('../../site/structuredData');

const LIVE_COURSE_PATH = '/courses/ai-trading-bootcamp';
const NEXT_CHALLENGE_CONTACT_URL =
  'mailto:support@botspot.trade?subject=Next%20AI%20Trading%20Challenge';
// Registration-page length policy (2026-08-07): our own PostHog test
// SALES_PAGE_DESIGN ended with the minimal hero+form control at a 26.59% Lead
// rate vs 21.50% for the decorated animated/FAQ variant and 9.68% for the
// video variant. Free lead capture converts best short: everything a visitor
// needs to say yes stays above or one scroll from the form.
const BOARD_STEPS = [
  {
    title: 'Set up together at kickoff',
    body: 'We set up your AI trading workflow together on camera. You leave with one clear project for the week.',
  },
  {
    title: 'Build one project during the week',
    body: 'Keep improving the same workflow. Short emails answer common setup questions. There is no daily homework.',
  },
  {
    title: 'Keep it running',
    body: 'The workflow is yours to keep improving. Reply to any challenge email with a question and a person answers.',
  },
];

const CHALLENGE_OUTCOMES = [
  {
    stage: 'Research',
    question: 'What changed?',
    detail: 'Market data · company context · news',
  },
  {
    stage: 'Plan',
    question: 'What would make this trade valid?',
    detail: 'Thesis · entry · risk · exit',
  },
  {
    stage: 'Order',
    question: 'Does every detail hold up?',
    detail: 'Paper or supported broker · final review',
  },
];

const CHALLENGE_LOGISTICS = [
  {
    name: 'One live session with Rob',
    detail: 'Live on Zoom, or watch on YouTube',
  },
  {
    name: 'Challenge hub',
    detail: 'Live links, calendar links, and any approved recordings',
  },
  {
    name: 'Help between sessions',
    detail: 'Short emails with setup help and next steps',
  },
];

const CHALLENGE_REQUIREMENTS = [
  {
    name: 'A computer and internet connection',
    detail: 'The live session runs on Zoom. No coding experience is required.',
  },
  {
    name: 'A paper or supported brokerage account',
    detail: 'Needed only when you are ready to prepare a paper or real order.',
  },
];

// Five objection-killers only. Time commitment lives in the board copy, so it
// is not repeated here.
const FAQS = [
  {
    q: 'Is this really free?',
    a: 'Yes. The live session and the week of tip emails are free. No credit card.',
  },
  {
    q: 'Do I need to know how to code?',
    a: 'No. Use BotSpot in plain English. Advanced users can connect their own coding agent, but nothing in the challenge requires it.',
  },
  {
    q: 'Do I need a broker?',
    a: 'To place a paper or real trade during the challenge, connect a supported broker. Rob covers setup at kickoff.',
  },
  {
    q: 'What if I miss the kickoff?',
    a: 'Register for the next show. It runs every week, and if an approved recording is published it will appear in the same hub.',
  },
  {
    q: 'Will you pitch something?',
    a: 'You will work on something real in the live session. At the end, Rob briefly explains the paid course options. No purchase is required.',
  },
];

const CONTROL_ROOM_FAQS = FAQS;

const WORKBENCH_COPY = {
  control_room: {
    metaDescription:
      'One live week to research an idea, define risk, and prepare an order you can explain.',
    titleLines: ['Take one trade', 'from idea'],
    titleEmphasis: 'evidence.',
    sub: 'Research it, define risk, and prepare an order during one free, focused week with Rob.',
    hostPrefix: 'Live with ',
    hostCreds: 'CEO of BotSpot · creator of Lumibot · built 1,000+ trading systems',
    format: 'Free to join · Seven focused days',
    kickoffSpan: 'Choose and build',
    wrapSpan: 'Return and review',
    briefHeading: 'Follow the decision, not the noise.',
    briefLede: 'Three checks turn one market idea into an order you can explain and review.',
    gateLabel: 'Final review',
    gate: 'You review every detail before anything is sent.',
    liveHeading: 'Seven days. One live room.',
    kickoffBody: 'Choose one idea and build its first working version together.',
    wrapBody: 'Bring your decisions back. Review gaps and next moves with Rob.',
    helpDetail: 'Short emails with setup help and next steps',
    brokerDetail: 'Needed only when you are ready to prepare a paper or real order.',
    mentorHeading: 'Rob built trading systems before teaching them.',
    mentorBio1:
      'Built Greystone loan software that processed $3B+ in transactions, then worked on trading and brokerage infrastructure as an early Voyager Digital engineer.',
    mentorBio2:
      'Rob created Lumibot, built more than 1,000 trading systems, and taught algorithmic and AI trading to more than 14,000 students.',
    showProof: true,
    reassure: 'Free. No credit card.',
    ctaLabel: 'Register free',
    formHeading: 'Register free',
    closingWithDate: 'Build one live.',
    closingWithoutDate: 'Build the next one live.',
  },
  clarity: {
    metaDescription:
      'Join the free 7-Day AI Trading Challenge. Research your trading idea with AI and prepare a trade to review with Rob, live.',
    titleLines: ['Take one trade', 'from idea'],
    titleEmphasis: 'evidence.',
    sub: 'Bring one trading idea. Research it with AI, explore the chart, and prepare a trade to review with Rob, live.',
    hostPrefix: '',
    hostCreds: 'CEO of BotSpot. Creator of Lumibot.',
    // Rob, 2026-09-11: the challenge is free. It is not a seven day free
    // trial, and "free for seven days" reads like one. Never reintroduce it.
    format: 'Free 7-day challenge.',
    kickoffSpan: '',
    wrapSpan: '',
    briefHeading: 'From your idea to a trade you can review',
    briefLede:
      'Start with a quote, explore the chart, then write the entry, risk, and exit for a trade you can review live.',
    gateLabel: 'Before you trade',
    gate: 'You choose the investment, trade size, and risk. Review and approve your order before it is sent to your broker.',
    liveHeading: 'One live session',
    kickoffBody: 'Set up with Rob, choose your trading idea, and start researching it with AI.',
    wrapBody:
      'Review your research and trade plan with Rob. Bring your progress, even if you are not finished.',
    helpDetail: 'Get short emails with setup help and next steps. No daily homework.',
    brokerRequirement: 'A supported brokerage account',
    brokerDetail:
      'Broker linking is optional until you prepare an order. Paper trading is available if you want to practice.',
    mentorHeading: 'Rob built trading systems before teaching them.',
    mentorBio1:
      'Built Greystone loan software that processed $3B+ in transactions, then worked on trading and brokerage infrastructure as an early Voyager Digital engineer.',
    mentorBio2:
      'Rob created Lumibot, built more than 1,000 trading systems, and taught algorithmic and AI trading to more than 14,000 students.',
    showProof: true,
    reassure: 'Free to join. No credit card required.',
    ctaLabel: 'Join the free challenge',
    formHeading: 'Join the free challenge',
    closingWithDate: 'Join the free challenge',
    closingWithoutDate: 'Ask for the next dates',
    outcomes: [
      {
        stage: 'Research',
        question: 'Start with a quote',
        detail: 'Prices, news, and the company',
      },
      {
        stage: 'Chart',
        question: 'Explore the chart',
        detail: 'Price history, volume, and indicators',
      },
      {
        stage: 'Order',
        question: 'Review a trade',
        detail: 'Entry, risk, exit, and your approval',
      },
    ],
  },
};

const RegistrationPhoneFields = ({
  form,
  setForm,
  disabled,
  testId,
  phoneError,
  clearPhoneError,
  tone = 'dark',
}) => (
  <>
    <label>
      Phone number
      <PhoneInput
        type="tel"
        name="phone"
        aria-invalid={phoneError ? true : undefined}
        aria-describedby={phoneError ? `${testId}-phone-error` : undefined}
        value={form.phone}
        onValueChange={(phone) => {
          clearPhoneError();
          setForm({
            ...form,
            phone,
            // Consent is number-specific. Editing a number requires a fresh choice.
            marketingSms: false,
          });
        }}
        disabled={disabled}
      />
    </label>
    {phoneError ? (
      <p id={`${testId}-phone-error`} className="cp-note" role="alert">
        {phoneError}
      </p>
    ) : null}
    <SmsConsentCheckbox
      id={`${testId}-marketing-sms`}
      checked={form.marketingSms}
      onChange={(checked) => setForm({ ...form, marketingSms: checked })}
      disabled={disabled || !form.phone.trim()}
      tone={tone}
      compact
    />
  </>
);

const RegistrationForm = ({
  form,
  setForm,
  onSubmit,
  submitting,
  error,
  phoneError,
  clearPhoneError,
  disabled,
  disabledReason,
  ctaLabel,
  testId,
  postPurchase = false,
  compact = false,
  heading,
  reassure = 'Free. No credit card.',
  questions = null,
  step = 'details',
}) => {
  // Rob, 2026-10-02: one form box, three steps, same page. Step 1 is the
  // fields below. Once the seat is saved, `questions` (ChallengeFormQuestions)
  // takes their place inside this same <form>. The box keeps its step 1
  // height while they show: a shorter box would pull the page below it up,
  // and the browser's scroll anchoring would then move the box itself off
  // screen (seen in Firefox on 2026-10-02).
  const formRef = useRef(null);
  const detailsHeight = useRef(null);
  useLayoutEffect(() => {
    if (!questions && formRef.current) {
      detailsHeight.current = formRef.current.getBoundingClientRect().height || null;
    }
  });
  // And scroll anchoring stays off while the questions show, so content that
  // arrives later (the "already registered" notice) cannot shift the box.
  const showingQuestions = Boolean(questions);
  useLayoutEffect(() => {
    if (!showingQuestions) return undefined;
    const root = document.documentElement;
    const previous = root.style.overflowAnchor;
    root.style.overflowAnchor = 'none';
    return () => {
      root.style.overflowAnchor = previous;
    };
  }, [showingQuestions]);
  return (
    <form
      ref={formRef}
      className={`cp-form chp-form-card${postPurchase ? ' chp-form-card--post-purchase' : ''}${questions ? ' chp-form-card--questions' : ''}`}
      style={
        questions && detailsHeight.current ? { minHeight: `${detailsHeight.current}px` } : undefined
      }
      onSubmit={questions ? (event) => event.preventDefault() : onSubmit}
      data-testid={testId}
      data-form-step={questions ? step : 'details'}
      autoComplete="on"
    >
      {questions || (
        <RegistrationDetails
          form={form}
          setForm={setForm}
          submitting={submitting}
          error={error}
          phoneError={phoneError?.formTestId === testId ? phoneError.message : null}
          clearPhoneError={clearPhoneError}
          disabled={disabled}
          disabledReason={disabledReason}
          ctaLabel={ctaLabel}
          testId={testId}
          postPurchase={postPurchase}
          compact={compact}
          heading={heading}
          reassure={reassure}
        />
      )}
    </form>
  );
};

/** Step 1 of the challenge form box: name, email, phone, consent, button. */
const RegistrationDetails = ({
  form,
  setForm,
  submitting,
  error,
  phoneError,
  clearPhoneError,
  disabled,
  disabledReason,
  ctaLabel,
  testId,
  postPurchase,
  compact,
  heading,
  reassure,
}) => (
  <>
    {/* heading={false} drops the title so the form-first arm fits the first
        phone screen. */}
    {heading === false ? null : (
      <h3>
        {postPurchase
          ? 'Join the free challenge'
          : heading || (compact ? 'Register free' : 'Save your free seat')}
      </h3>
    )}
    <label>
      Name
      <input
        type="text"
        name="name"
        required
        autoComplete="name"
        placeholder="Your name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        disabled={disabled}
      />
    </label>
    <label>
      Email
      <input
        type="email"
        name="email"
        required
        autoComplete="email"
        placeholder="you@example.com"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        disabled={disabled}
      />
    </label>
    <RegistrationPhoneFields
      form={form}
      setForm={setForm}
      disabled={disabled}
      testId={testId}
      phoneError={phoneError}
      clearPhoneError={clearPhoneError}
      tone={compact ? 'light' : 'dark'}
    />
    {disabled && disabledReason ? (
      <p className="chp-unavailable" role="status">
        {disabledReason}
      </p>
    ) : null}
    <button
      type="submit"
      className="cp-btn-primary"
      disabled={submitting || disabled}
      data-testid={`${testId}-submit`}
      title={disabled ? disabledReason : undefined}
    >
      {submitting ? 'Saving your seat...' : ctaLabel}
    </button>
    {error ? (
      <p className="cp-note" role="alert">
        {error}
      </p>
    ) : null}
    <p className="chp-reassure">{reassure}</p>
  </>
);

// Checked the moment Register is pressed, while the catalog may still be
// loading. Rob, 2026-10-21: the phone never blocks a signup and is never
// dropped. The shared PhoneInput hints softly at a number it cannot read, and
// the server keeps it as typed. Only a consent tick with no number is a
// problem here.
const challengePhoneProblem = ({ phone, marketingSms }) => {
  const trimmed = String(phone || '').trim();
  if (!trimmed && marketingSms) {
    return 'Enter a phone number to receive texts, or untick text consent to continue.';
  }
  return null;
};

/**
 * Free challenge landing page and, once registered, the participant hub.
 *
 * Sessions come from the course / session-group / course-session tables.
 * If the catalog cannot load, purchase/registration is disabled loudly.
 */
const ChallengePage = ({ presentation, experimentContext, publicCatalog } = {}) => {
  // /challenges carries no slug (Rob, 2026-09-11: per-run URLs are gone) and
  // shows the current run from the catalog. The slug param only remains for
  // the internal /challenges-clarity/:slug review route.
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const educationDesignVariant = useEducationDesignVariant();
  const controlRoom = isEducationWorkbenchVariant(educationDesignVariant);
  const isClarity = educationDesignVariant === 'clarity';
  const isFormFirst = educationDesignVariant === 'form_first';
  const workbenchCopy = isClarity ? WORKBENCH_COPY.clarity : WORKBENCH_COPY.control_room;
  const workbenchLogistics = CHALLENGE_LOGISTICS.map((item) =>
    item.name === 'Help between sessions' ? { ...item, detail: workbenchCopy.helpDetail } : item,
  );
  const workbenchRequirements = CHALLENGE_REQUIREMENTS.map((item) =>
    item.name === 'A paper or supported brokerage account'
      ? {
          ...item,
          name: workbenchCopy.brokerRequirement || item.name,
          detail: workbenchCopy.brokerDetail,
        }
      : item,
  );
  const workbenchOutcomes = workbenchCopy.outcomes || CHALLENGE_OUTCOMES;
  // Only explicit review overrides continue to the course. Live assignments
  // remain independent, so this never propagates a challenge experiment flag.
  const educationCoursePath = isClarity
    ? preserveEducationPreviewParams(LIVE_COURSE_PATH, searchParams.toString())
    : LIVE_COURSE_PATH;
  const {
    user,
    isAuthenticated,
    isLoading: userLoading,
    loginWithRedirect,
    getApiToken,
  } = useUser();
  const [challenge, setChallenge] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [scheduleAttempt, setScheduleAttempt] = useState(0);
  const [registered, setRegistered] = useState(false);
  // A 409 from register means this email already has a seat. That is a success
  // for the person: they get the same two questions and the calendar, and
  // their links are emailed again.
  const [resendConfirmed, setResendConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [phoneError, setPhoneError] = useState(null);
  const clearPhoneError = () => setPhoneError(null);
  // A Register press that lands before the catalog names the run. It is held,
  // never dropped, and sent the moment the run is known. It keeps the values
  // exactly as they were when the visitor pressed the button.
  const [heldRegistration, setHeldRegistration] = useState(null);
  // Steps 2 and 3 (Rob, 2026-09-30, moved inside the form box 2026-10-02):
  // after the seat is saved, two tap questions in the same box. Holds what
  // they need to save the answers and continue to the welcome page, and which
  // form box (hero or bottom) asks them.
  const [qualifying, setQualifying] = useState(null);
  // Rob, 2026-10-22: a signup who closes the page during the two questions has
  // left without booking, so the AI welcome call comes sooner. Moving on to the
  // booking page is not leaving; that page takes over the signal.
  useLeftWithoutBooking(qualifying?.readToken || null);
  const [qualifyingQuestion, setQualifyingQuestion] = useState('investingExperience');
  // A second final tap never reports the answers to PostHog twice.
  const qualifiedLeadSentForRef = useRef(null);
  const [submission, setSubmission] = useState({
    description: '',
    publicLink: '',
    question: '',
    displayName: '',
    showcasePermission: false,
    screenshotFile: null,
  });
  const [submissionSaving, setSubmissionSaving] = useState(false);
  const [submissionSaved, setSubmissionSaved] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    marketingSms: false,
  });
  const isPostPurchase = searchParams.get('post_purchase') === 'true';
  // Arms C and D (Rob, 2026-10-20): A's page with the repeats removed. Only the
  // public A layout is trimmed; a post-purchase visitor keeps A as it is.
  const trimmedCopy = useChallengeTrimmedCopy();
  const trimmed = Boolean(trimmedCopy) && !controlRoom && !isFormFirst && !isPostPurchase;
  // C and D hide the site header's menu links and keep the logo: about 1 in
  // 15 visitors clicked one and left (2026-10-21 research). A and B keep them.
  useHideSiteHeaderMenuLinks(trimmed);
  const postPurchaseDestination = sanitizeInternalDestination(
    searchParams.get('redirectTo'),
    '/account-settings/subscriptions',
  );
  const retrySchedule = () => {
    setIsLoading(true);
    setLoadError(null);
    if (publicCatalog) publicCatalog.retry();
    else setScheduleAttempt((attempt) => attempt + 1);
  };

  useEffect(() => {
    if (!controlRoom || registered) return undefined;

    const root = document.getElementById('challenge-top');
    const motionItems = Array.from(root?.querySelectorAll('[data-chx-reveal]') || []);
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    const supportsIntersectionObserver = 'IntersectionObserver' in window;

    if (reducedMotion || !supportsIntersectionObserver) {
      motionItems.forEach((item) => item.setAttribute('data-chx-reveal', 'visible'));
      return undefined;
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.setAttribute('data-chx-reveal', 'visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -4% 0px' },
    );

    motionItems.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, [controlRoom, registered]);

  useEffect(() => {
    let mounted = true;
    const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';
    setIsLoading(true);
    setLoadError(null);
    setChallenge(null);
    setRegistered(false);

    (async () => {
      try {
        // The run's dates and status are public. This read never waits on the
        // visitor's login, so a slow Auth0 answer cannot hold the form hostage.
        // A signed-in visitor's own seat is looked up separately below.
        // Public filter keeps entitled cohorts from burying challenge runs.
        if (publicCatalog?.isLoading) return;
        if (publicCatalog?.loadFailed) throw new Error('session_groups_unavailable');
        const groups = publicCatalog
          ? publicCatalog.groups || []
          : await readPublicChallengeCatalog(API_URL);

        const match = slug ? selectChallengeRun(groups, slug) : selectCurrentChallengeRun(groups);
        if (!match) throw new Error('challenge_not_found');

        if (!mounted) return;
        // Catalog dates and registration access are sufficient to save a
        // seat. Session/replay enrichment must not hold a mobile submission.
        setChallenge(match);
        setRegistered(Boolean(match?.isRegistered || match?.registration));
        setLoadError(null);
        setIsLoading(false);
        fetch(`${API_URL}/courses/session-groups/${match.id}`)
          .then(async (response) => {
            if (!response.ok) return;
            const detail = await response.json();
            if (!mounted) return;
            setChallenge((current) =>
              current?.id === match.id ? { ...current, ...detail } : current,
            );
            // A late anonymous response cannot undo a seat just saved by the
            // visitor, or a signed-in lookup that completed independently.
            if (detail?.isRegistered || detail?.registration) setRegistered(true);
          })
          .catch(() => {
            // Optional session details failing does not invalidate the public
            // catalog or a confirmed registration.
          });
      } catch (err) {
        if (!mounted) return;
        setChallenge(null);
        setLoadError(
          'Challenge schedule is temporarily unavailable. Registration is disabled until dates load from the catalog.',
        );
      } finally {
        if (mounted && !publicCatalog?.isLoading) setIsLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [slug, scheduleAttempt, publicCatalog]);

  // A signed-in visitor may already hold a seat. That lookup rides on their
  // token, so it runs beside the public read and never delays the form.
  useEffect(() => {
    if (!isAuthenticated || !challenge?.id) return undefined;
    let mounted = true;
    const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';
    const challengeId = challenge.id;

    (async () => {
      try {
        const token = await getApiToken().catch(() => null);
        if (!token || !mounted) return;
        const res = await fetch(`${API_URL}/courses/session-groups/${challengeId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || !mounted) return;
        const detail = await res.json();
        if (!mounted) return;
        setChallenge((current) =>
          current?.id === challengeId ? { ...current, ...detail } : current,
        );
        if (detail?.isRegistered || detail?.registration) setRegistered(true);
      } catch {
        // The public page stays fully usable without the member lookup.
      }
    })();

    return () => {
      mounted = false;
    };
  }, [isAuthenticated, challenge?.id, getApiToken]);

  useEffect(() => {
    if (user?.email) {
      setForm((prev) => {
        const profileName =
          [user.firstName, user.lastName].filter(Boolean).join(' ') ||
          String(user.name || '').trim();
        const profilePhone = String(user.phone || '').trim();

        return {
          ...prev,
          email: prev.email || user.email,
          name: prev.name || profileName,
          phone: prev.phone || profilePhone,
          // Prefill contact details, never a new affirmative consent decision.
          // Only the visitor checking this form can create an accepted audit.
          marketingSms: prev.marketingSms,
        };
      });
    }
  }, [user]);

  useEffect(() => {
    if (!registered || !isAuthenticated || !challenge?.id) return undefined;
    let mounted = true;
    const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';

    (async () => {
      try {
        const token = await getApiToken();
        if (!token) return;
        const res = await fetch(`${API_URL}/courses/session-groups/${challenge.id}/submission`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || !mounted) return;
        const payload = await res.json();
        if (!payload?.submission) return;
        setSubmission((current) => ({
          ...current,
          description: payload.submission.description || '',
          publicLink: payload.submission.publicLink || '',
          question: payload.submission.question || '',
          displayName: payload.submission.displayName || '',
          showcasePermission: payload.submission.showcasePermission === true,
        }));
        setSubmissionSaved(true);
      } catch {
        // An unavailable optional share form must not hide the challenge hub.
      }
    })();

    return () => {
      mounted = false;
    };
  }, [registered, isAuthenticated, challenge?.id, getApiToken]);

  // The challenge is one live show. pickKickoffAndDemo still returns a demoDay
  // for any run that carries a second session row, but nothing on this page
  // renders it any more. Rob, 2026-09-22: "There is no part two."
  const { kickoff, sessions } = useMemo(
    () => pickKickoffAndDemo(challenge?.sessions || []),
    [challenge],
  );

  // List payload may only expose nextSession when detail is down.
  const kickoffAt =
    kickoff?.scheduledAt || challenge?.nextSession?.scheduledAt || challenge?.scheduledAt || null;

  const kickoffLabel = formatChallengeDateTime(kickoffAt);
  const kickoffCalendarLinks = kickoff
    ? buildChallengeCalendarLinks(kickoff, getApiBaseUrl() || 'https://api.botspot.trade')
    : null;
  const kickoffTime = kickoffAt ? new Date(kickoffAt).getTime() : Number.NaN;
  const hasFutureKickoff = Number.isFinite(kickoffTime) && kickoffTime > Date.now();
  const registrationOpen =
    !isLoading &&
    !loadError &&
    Boolean(challenge?.id) &&
    challenge?.registrationAccess === 'public' &&
    hasFutureKickoff;
  // Rob, 2026-09-23: "The register button should be ready from the moment the
  // page loads. We do not need a login check or anything like that." The form
  // is usable from the first render. It locks only once the catalog has
  // actually answered and that answer is not an open, public, future run.
  const registrationClosed = !isLoading && !registrationOpen;
  const awaitingCatalogForHeldPress = Boolean(heldRegistration) && isLoading;
  const registrationStatus = loadError
    ? {
        title: 'Schedule could not load',
        body: 'Registration stays locked until current dates load from the catalog.',
      }
    : kickoffAt && !hasFutureKickoff
      ? {
          title: 'This challenge has started',
          body: 'Ask about the next live run, or compare the five-week course.',
        }
      : {
          title: 'Next dates not posted',
          body: 'Registration opens when the next kickoff date is published.',
        };

  // Crawler documents for /challenges are a build-time snapshot of this render.
  // Settled means the catalog read succeeded and auth resolved, so the form
  // shows its real label. A failed read never marks the page ready.
  const prerenderReady = !isLoading && !userLoading && !loadError && Boolean(challenge?.id);
  const prerenderState = registrationOpen ? 'scheduled' : hasFutureKickoff ? 'closed' : 'started';

  const details = useMemo(
    () => [
      {
        label: 'Live kickoff',
        value: kickoffLabel || (isLoading ? 'Loading...' : 'Unavailable'),
      },
      { label: 'Length', value: '7 days' },
      { label: 'Price', value: 'Free' },
    ],
    [kickoffLabel, isLoading],
  );

  const submitRegistration = useCallback(
    async (values, utmData, formTestId = 'challenge-form') => {
      setSubmitting(true);
      setError(null);
      const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';

      try {
        if (!challenge?.id || !registrationOpen) {
          throw new Error('challenge_not_ready');
        }
        const phoneProblem = challengePhoneProblem(values);
        if (phoneProblem) throw new Error(phoneProblem);
        const { firstName, lastName } = splitDisplayName(values.name);
        // PhoneInput already hands over E.164, or exactly what was typed.
        // Rob, 2026-10-21: "I would not want to lose their number."
        const phone = String(values.phone || '').trim();
        const measurementAttribution = getWebsiteThemeAttribution();
        const posthogDistinctId =
          measurementAttribution?.posthog_distinct_id || window.posthog?.get_distinct_id?.();
        const body = {
          ...measurementAttribution,
          firstName,
          lastName,
          email: values.email,
          registrationContext: 'public_challenge',
          challengeAssignmentCaptured: true,
          googleAdsConsent: getChallengeGoogleAdsConsent(),
          posthogDistinctId: posthogDistinctId?.slice?.(0, 200),
          ...(experimentContext?.is_experiment_active && {
            challengeExperiment: {
              name: experimentContext.experiment_name,
              flag: experimentContext.feature_flag,
              variant: experimentContext.experiment_variant,
              active: true,
              ...(experimentContext.experiment_version != null && {
                version: experimentContext.experiment_version,
              }),
            },
          }),
          utmSource: utmData.utm_source || undefined,
          utmMedium: utmData.utm_medium || undefined,
          utmCampaign: utmData.utm_campaign || undefined,
          utmTerm: utmData.utm_term || undefined,
          utmContent: utmData.utm_content || undefined,
          gclid: utmData.gclid || undefined,
          gbraid: utmData.gbraid || undefined,
          wbraid: utmData.wbraid || undefined,
          fbclid: utmData.fbclid || undefined,
          fbp: utmData.fbp || undefined,
          fbc: utmData.fbc || undefined,
          googleCampaignId: utmData.utm_campaign_id || undefined,
          googleAssetGroupId: utmData.utm_asset_group || undefined,
          googleCreativeId: utmData.utm_creative || undefined,
          googleDevice: utmData.utm_device || undefined,
          googleNetwork: utmData.utm_network || undefined,
          googlePlacement: utmData.utm_placement || undefined,
          googlePhysicalLocationId: utmData.utm_location || undefined,
          metaCampaignId: utmData.utm_campaign_id || undefined,
          metaAdSetId: utmData.utm_adset_id || undefined,
          metaAdId: utmData.utm_ad_id || undefined,
          firstTouchAttribution: Object.fromEntries(
            Object.entries(utmData).filter(
              ([key, value]) => key.startsWith('first_touch_') && value,
            ),
          ),
          referrer: utmData.referrer || document.referrer || undefined,
          landingPage: utmData.landing_page || window.location.href.split('?')[0],
          // Stored as user.signupTimeZone so the AI welcome call stays inside
          // legal calling hours (Rob, 2026-10-21). Node validates it.
          signupTimeZone: getBrowserTimeZone(),
        };

        // Phone is optional; supplying one still requires an explicit accepted
        // OR declined marketing decision. Never infer consent from this button.
        if (phone) {
          body.phone = phone;
          body.smsMarketingConsent = buildSmsConsentPayload({
            source: 'ai_trading_challenge_registration',
            phoneNumber: phone,
            accepted: values.marketingSms,
            pagePath: window.location.pathname,
          });
        }

        const token = isAuthenticated ? await getApiToken().catch(() => null) : null;
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/courses/session-groups/${challenge.id}/register`, {
          method: 'POST',
          keepalive: true,
          headers,
          body: JSON.stringify(body),
        });
        // A 409 means this email already has a seat. That is a success for
        // the person: they get the same two questions and the calendar
        // (Rob, 2026-10-02), and their links are emailed again.
        const alreadyRegistered = res.status === 409;
        if (alreadyRegistered) {
          // Best effort. A missing endpoint or a network failure never stops
          // the questions.
          fetch(
            `${API_URL}/courses/session-groups/${challenge.id}/registration/resend-confirmation`,
            { method: 'POST', headers, body: JSON.stringify({ email: values.email }) },
          )
            .then((resendRes) => {
              if (resendRes?.ok) setResendConfirmed(true);
            })
            .catch(() => {
              /* The questions and the welcome page still follow. */
            });
        } else if (!res.ok) {
          const payload = await res.json().catch(() => ({}));
          throw new Error(payload.message || payload.error || 'registration_failed');
        }

        const payload = await res.json().catch(() => ({}));
        if (!payload?.registration?.id) {
          throw new Error('We could not confirm your seat. Please try again.');
        }
        if (!alreadyRegistered) {
          trackLeadSubmit({
            googleAdsConversion: false,
            ...getChallengeExperimentEventProperties(experimentContext),
            source: window.location.pathname,
            slug,
            ...utmData,
            google_campaign_id: utmData.utm_campaign_id,
            google_asset_group_id: utmData.utm_asset_group,
            google_creative_id: utmData.utm_creative,
            google_device: utmData.utm_device,
            google_network: utmData.utm_network,
            google_placement: utmData.utm_placement,
            google_physical_location_id: utmData.utm_location,
            registration_id: payload.registration.id,
          });
        }
        // Confirmed internal outcomes follow the ACK. Google already received
        // the independent submission signal before the request started.
        // Keep booking independent from authentication. Session storage
        // prefills Calendly without putting personal data in URL.
        const bookedChallengeSlug = normalizeChallengeSlug(challenge.name);
        const bookingContext = {
          email: values.email,
          name: values.name,
          registrationId: payload?.registration?.id || null,
          bookingReference: payload?.registration?.bookingReference || null,
          sessionGroupId: challenge.id,
          slug: bookedChallengeSlug,
        };
        try {
          window.sessionStorage.setItem('challengeBookingContext', JSON.stringify(bookingContext));
        } catch {
          // The server already saved the seat; optional booking prefill must
          // never turn a confirmed registration into a retryable failure.
        }
        // The seat is saved. The same form box now asks the two questions,
        // for new or incomplete returning registrations. Completed returning
        // registrations continue to the calendar; answers require a signed reference.
        if (payload?.registration?.qualificationComplete === true) {
          navigate('/challenges/welcome', { state: { bookingContext } });
          return;
        }
        if (!bookingContext.bookingReference?.readToken) {
          throw new Error('Your seat is saved. Please try again to finish the questions.');
        }
        qualifiedLeadSentForRef.current = null;
        setQualifyingQuestion('investingExperience');
        setQualifying({
          formTestId,
          alreadyRegistered,
          bookingContext,
          phone,
          readToken: payload?.registration?.bookingReference?.readToken || null,
        });
      } catch (err) {
        setError(
          err?.message && !String(err.message).includes('_')
            ? err.message
            : 'That did not go through. Please try again, or email support@botspot.trade and we will add you manually.',
        );
      } finally {
        setSubmitting(false);
      }
    },
    [challenge, registrationOpen, isAuthenticated, getApiToken, experimentContext, slug, navigate],
  );

  const register = useCallback(
    (event) => {
      event.preventDefault();
      const utmData = getUtmParamsForApi() || {};
      // Rob's intentional acquisition contract: dispatch Google on valid submit,
      // before awaiting authentication, registration or persistence. Internal
      // confirmed outcomes still require the server acknowledgement.
      const values = { ...form };
      // The box that was pressed (hero or bottom) asks the questions.
      const formTestId = event.currentTarget?.getAttribute?.('data-testid') || 'challenge-form';
      const phoneProblem = challengePhoneProblem(values);
      if (phoneProblem) {
        setError(null);
        setPhoneError({ message: phoneProblem, formTestId });
        // Focus on every press, even when the validation message has not changed.
        const phoneInput = event.currentTarget.querySelector('input[name="phone"]');
        phoneInput?.focus();
        phoneInput?.scrollIntoView?.({ block: 'center' });
        pushEvent({
          event: 'challenge_registration_validation_blocked',
          field: 'phone',
          reason: 'sms_consent_without_phone',
          source: window.location.pathname,
          slug,
          googleAdsConversion: false,
          ...getChallengeExperimentEventProperties(experimentContext),
        });
        return;
      }
      setPhoneError(null);
      sendChallengeSeatLead({
        // The catalog can resolve between the first press and a retry. Keep
        // this browser submission scope stable throughout that transition.
        registrationId: getChallengeLeadSubmissionId(slug || 'active-challenge', values.email),
        email: values.email,
        phone: values.phone,
      });
      pushEvent({
        event: 'challenge_registration_attempt',
        googleAdsConversion: false,
        ...getChallengeExperimentEventProperties(experimentContext),
        source: window.location.pathname,
        slug,
        utm_source: utmData.utm_source,
        utm_medium: utmData.utm_medium,
        utm_campaign: utmData.utm_campaign,
        utm_term: utmData.utm_term,
        utm_content: utmData.utm_content,
        gclid: utmData.gclid,
        gbraid: utmData.gbraid,
        wbraid: utmData.wbraid,
        google_campaign_id: utmData.utm_campaign_id,
        google_asset_group_id: utmData.utm_asset_group,
        google_creative_id: utmData.utm_creative,
        google_device: utmData.utm_device,
        google_network: utmData.utm_network,
        google_placement: utmData.utm_placement,
        google_physical_location_id: utmData.utm_location,
      });
      setError(null);
      if (isLoading) {
        // The catalog has not named the run yet. Hold the press and show
        // progress; the effect below sends it the moment the run is known.
        setHeldRegistration({ values, utmData, formTestId });
        return;
      }
      submitRegistration(values, utmData, formTestId);
    },
    [form, slug, isLoading, submitRegistration, experimentContext],
  );

  const postQualifyingAnswers = useCallback(async (readToken, body) => {
    if (!readToken) throw new Error('Missing answer-save reference.');
    const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';
    const response = await fetch(`${API_URL}/courses/challenge-registrations/qualifying-answers`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${readToken}` },
      body: JSON.stringify({
        ...body,
        questionVersion: CHALLENGE_QUESTION_VERSION,
        leadValueModelVersion: CHALLENGE_LEAD_VALUE_MODEL_VERSION,
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload.saved !== true)
      throw new Error('Answer save was not acknowledged.');
  }, []);

  const answerQualifying = useCallback(
    async (answers, changed) => {
      if (!qualifying) throw new Error('Missing saved seat.');
      const complete =
        changed === 'courseSpend' && Boolean(answers.investingExperience && answers.courseSpend);
      const registrationId = qualifying.bookingContext.registrationId;
      const value = challengeQualifyingLeadValue(answers);
      let tagProcessed = Promise.resolve();
      let conversion;
      if (complete) {
        // Deliberate submission signal (Rob, 2026-10-04): Google dispatches
        // alongside the save, even if our request fails or its response is lost.
        tagProcessed = new Promise((resolve) => {
          conversion = sendChallengeQualifiedLeadConversion({
            registrationId,
            ...answers,
            onSent: resolve,
          });
        });
        // This narrower outcome has its own action and deduplication. It must
        // dispatch even when an earlier all-survey hit succeeded but saving failed.
        const goodFitConversion = sendChallengeGoodFitLeadConversion({
          registrationId,
          ...answers,
        });
        if (goodFitConversion.fired)
          pushEvent({
            ...getChallengeExperimentEventProperties(experimentContext),
            event: 'challenge_good_fit_submitted',
            registration_id: registrationId,
            source: window.location.pathname,
            qualification_question_version: CHALLENGE_QUESTION_VERSION,
            good_fit_rule_version: CHALLENGE_GOOD_FIT_RULE_VERSION,
            investing_experience: answers.investingExperience,
            course_spend: answers.courseSpend,
            lead_value: value,
            google_ads_tag_invoked: true,
          });
        if (conversion.fired)
          pushEvent({
            ...getChallengeExperimentEventProperties(experimentContext),
            event: 'challenge_qualifying_submitted',
            good_fit: isChallengeGoodFit(answers),
            good_fit_rule_version: CHALLENGE_GOOD_FIT_RULE_VERSION,
            source: window.location.pathname,
            qualification_question_version: CHALLENGE_QUESTION_VERSION,
            investing_experience: answers.investingExperience,
            course_spend: answers.courseSpend,
            lead_value: value,
            lead_value_model_version: CHALLENGE_LEAD_VALUE_MODEL_VERSION,
            google_ads_tag_invoked: true,
            registration_id: registrationId,
          });
      }
      // The final save contains both answers and remains retryable. This ACK
      // controls the internal saved outcome and navigation, never Google's send.
      await postQualifyingAnswers(qualifying.readToken, {
        ...(complete ? answers : { [changed]: answers[changed] }),
        ...(complete && {
          googleAdsLead: { value, trigger: 'answered', dispatchStatus: 'prepared' },
        }),
      });
      if (!complete) return;
      if (qualifiedLeadSentForRef.current !== registrationId) {
        qualifiedLeadSentForRef.current = registrationId;
        trackChallengeQualifyingAnswered({
          ...answers,
          value,
          trigger: 'answered',
          googleAdsLeadSent: wasChallengeQualifiedLeadDispatched(registrationId),
          experimentContext,
        });
      }
      await tagProcessed;
      navigate('/challenges/welcome', { state: { bookingContext: qualifying.bookingContext } });
    },
    [qualifying, postQualifyingAnswers, experimentContext, navigate],
  );

  // Send a held press as soon as the catalog names the run. A failed read keeps
  // it, so Try again finishes the save. A visitor the catalog already shows as
  // registered sees their seat instead, and a closed run shows why it closed.
  useEffect(() => {
    if (!heldRegistration || isLoading || loadError) return;
    setHeldRegistration(null);
    if (registered || !registrationOpen) return;
    submitRegistration(
      heldRegistration.values,
      heldRegistration.utmData,
      heldRegistration.formTestId,
    );
  }, [heldRegistration, isLoading, loadError, registered, registrationOpen, submitRegistration]);

  const saveSubmission = useCallback(
    async (event) => {
      event.preventDefault();
      setSubmissionSaving(true);
      setSubmissionError(null);
      const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';

      try {
        const token = await getApiToken();
        if (!token) throw new Error('Sign in before sharing your project.');

        let screenshotUrl;
        if (submission.screenshotFile) {
          const uploadBody = new FormData();
          uploadBody.append('file', submission.screenshotFile);
          const uploadRes = await fetch(
            `${API_URL}/courses/session-groups/${challenge.id}/submission-image`,
            {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
              body: uploadBody,
            },
          );
          const uploadPayload = await uploadRes.json().catch(() => ({}));
          if (!uploadRes.ok) {
            throw new Error(uploadPayload.error || 'Could not upload that screenshot.');
          }
          screenshotUrl = uploadPayload.screenshotUrl;
        }

        const res = await fetch(`${API_URL}/courses/session-groups/${challenge.id}/submission`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            description: submission.description,
            publicLink: submission.publicLink || undefined,
            screenshotUrl,
            question: submission.question || undefined,
            displayName: submission.displayName || undefined,
            showcasePermission: submission.showcasePermission,
          }),
        });
        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload.error || 'Could not save your project.');
        setSubmissionSaved(true);
        setSubmission((current) => ({ ...current, screenshotFile: null }));
      } catch (err) {
        setSubmissionError(err?.message || 'Could not save your project.');
      } finally {
        setSubmissionSaving(false);
      }
    },
    [challenge?.id, getApiToken, submission],
  );

  const focusRegistrationForm = () => {
    window.requestAnimationFrame(() => {
      document.querySelector('#challenge-register input:not([disabled])')?.focus({
        preventScroll: true,
      });
    });
  };

  const runTitle = canonicalChallengeRunName(challenge?.name);
  const challengeName = String(
    challenge?.course?.title || challenge?.name || AI_TRADING_CHALLENGE_NAME,
  ).trim();
  const catalogDescription = String(challenge?.description || '').trim();
  const defaultChallengeDescription = isClarity
    ? workbenchCopy.metaDescription
    : 'Pick one investment. Research it with AI. Prepare a paper or real trade, then review the setup live with Rob.';
  const pageTitle = `${challengeName} | Free Live Challenge | BotSpot`;
  const pageDescription =
    !controlRoom || isClarity
      ? catalogDescription || defaultChallengeDescription
      : workbenchCopy.metaDescription;
  const availableReplays = sessions.filter((session) => isChallengeReplayAvailable(session));
  // Event schema for the free kickoff, from the loaded catalog row only.
  const eventSchema = prerenderReady
    ? challengeEventStructuredData({
        path: '/challenges',
        name: challengeName,
        description: pageDescription,
        startDate: kickoffAt,
        isFree: true,
      })
    : null;

  const pageHead = (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {eventSchema ? (
        <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
      ) : null}
    </Helmet>
  );

  // Steps 2 and 3 render inside the form box whose button saved the seat.
  const questionsFor = (testId) =>
    qualifying?.formTestId === testId ? (
      <ChallengeFormQuestions
        key={qualifying.bookingContext.registrationId || 'seat'}
        current={qualifyingQuestion}
        onShow={setQualifyingQuestion}
        onAnswer={answerQualifying}
        notice={
          qualifying.alreadyRegistered ? (
            <>
              <span>You&apos;re already registered.</span>
              {resendConfirmed ? (
                <>
                  {' '}
                  <span>We sent your links again.</span>
                </>
              ) : null}
            </>
          ) : null
        }
      />
    ) : null;

  // Share the exact registration and recovery UI across review presentations.
  const registrationPanel = (
    <>
      {registered ? (
        <Reveal instant delay={120}>
          <div
            className="chp-registered"
            data-testid="challenge-registered"
            id="challenge-confirmation"
            role="status"
            tabIndex="-1"
          >
            <h2>You are in.</h2>
            <p>{kickoffLabel ? `Kickoff is ${kickoffLabel}.` : 'Your challenge seat is saved.'}</p>
            <div className="chp-registered-actions" aria-label="Live kickoff links">
              {kickoff?.joinUrl ? (
                <a
                  className="chp-live-action chp-live-action--primary"
                  href={kickoff.joinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Join live on Zoom
                </a>
              ) : null}
              {youtubeLiveUrl(kickoff) ? (
                <a
                  className="chp-live-action chp-live-action--secondary"
                  href={youtubeLiveUrl(kickoff)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get notified on YouTube
                </a>
              ) : null}
            </div>
            {kickoffCalendarLinks ? (
              <div className="chp-registered-calendar">
                <p>Add the kickoff to your calendar</p>
                <div className="chp-registered-calendar-actions">
                  <a
                    href={kickoffCalendarLinks.google}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="challenge-registered-google-calendar"
                  >
                    Google Calendar
                  </a>
                  <a
                    href={kickoffCalendarLinks.ics}
                    download={`${canonicalChallengeSessionTitle(kickoff.title)}.ics`}
                    data-testid="challenge-registered-ics"
                  >
                    Apple / Outlook
                  </a>
                </div>
              </div>
            ) : null}
            {kickoffAt ? (
              <div className="chp-registered-countdown">
                <PromoCountdown
                  endsAt={kickoffAt}
                  label="Kickoff starts in"
                  variant={controlRoom ? 'words' : 'blocks'}
                />
              </div>
            ) : null}
          </div>
        </Reveal>
      ) : !isLoading && !registrationOpen ? (
        <div className="chp-registration-status" id="challenge-register" role="status">
          <h2>{registrationStatus.title}</h2>
          <p>
            {presentation === 'clarity-home' && loadError
              ? 'We couldn’t load the live dates. Try again to see the schedule and save your seat.'
              : registrationStatus.body}
          </p>
          {loadError && heldRegistration ? (
            <p data-testid="challenge-held-registration">
              Your details are still filled in. Press Try again to finish saving your seat.
            </p>
          ) : null}
          <div className="chp-registration-status__actions">
            {loadError ? (
              <button type="button" className="cp-btn-primary" onClick={retrySchedule}>
                Try again
              </button>
            ) : null}
            <a
              className={loadError ? undefined : 'cp-btn-primary'}
              href={NEXT_CHALLENGE_CONTACT_URL}
            >
              Ask about the next live week
            </a>
            <Link to={educationCoursePath}>See the five-week course</Link>
          </div>
        </div>
      ) : (
        <Reveal instant delay={120}>
          <div id="challenge-register">
            <RegistrationForm
              form={form}
              setForm={setForm}
              onSubmit={register}
              submitting={submitting || awaitingCatalogForHeldPress}
              error={error}
              phoneError={phoneError}
              clearPhoneError={clearPhoneError}
              // Registration is anonymous, so the button never waits on
              // Auth0 or on the catalog. On a slow phone either wait meant
              // a visitor could fill the form, press the button and have
              // nothing happen. Only a settled, closed answer locks it.
              disabled={registrationClosed}
              disabledReason={
                loadError ||
                (!kickoffAt && !isLoading
                  ? 'Kickoff date is missing from the catalog, so registration is locked.'
                  : null)
              }
              ctaLabel={
                isPostPurchase
                  ? 'Join the free challenge'
                  : controlRoom
                    ? workbenchCopy.ctaLabel
                    : trimmed
                      ? CHALLENGE_TRIMMED_CTA
                      : 'Save my free seat'
              }
              testId="challenge-form"
              questions={questionsFor('challenge-form')}
              step={qualifyingQuestion}
              postPurchase={isPostPurchase}
              compact={controlRoom}
              heading={
                trimmed
                  ? false
                  : controlRoom && !isPostPurchase
                    ? workbenchCopy.formHeading
                    : undefined
              }
              reassure={
                controlRoom
                  ? workbenchCopy.reassure
                  : trimmed
                    ? CHALLENGE_TRIMMED_REASSURE
                    : 'Free. No credit card.'
              }
            />
          </div>
        </Reveal>
      )}
    </>
  );

  // Rob, 2026-09-23: the C arm of the challenge ABC test. Short copy and the
  // whole form on the first phone screen. It renders the same form, press
  // handling and consent as control. A registered visitor and the
  // post-purchase handoff keep the control hub below.
  if (isFormFirst && !registered && !isPostPurchase) {
    return (
      <div
        className="course-page challenge-page challenge-page--form-first"
        data-testid="challenge-page"
        data-challenge-layout="form_first"
        id="challenge-top"
        role="main"
        tabIndex="-1"
      >
        <PrerenderReady ready={prerenderReady} state={prerenderState} />
        {pageHead}
        <ChallengePageFormFirst
          challengeName={challengeName}
          registrationForm={
            <RegistrationForm
              form={form}
              setForm={setForm}
              onSubmit={register}
              submitting={submitting || awaitingCatalogForHeldPress}
              error={error}
              phoneError={phoneError}
              clearPhoneError={clearPhoneError}
              disabled={registrationClosed}
              disabledReason={loadError}
              ctaLabel={FORM_FIRST_CTA}
              testId="challenge-form"
              questions={questionsFor('challenge-form')}
              step={qualifyingQuestion}
              heading={false}
              reassure="Free. No credit card."
            />
          }
          registrationClosed={registrationClosed}
          registrationStatus={registrationStatus}
          loadError={loadError}
          heldRegistration={heldRegistration}
          onRetrySchedule={retrySchedule}
          kickoffLabel={kickoffLabel}
          isLoading={isLoading}
          contactUrl={NEXT_CHALLENGE_CONTACT_URL}
          coursePath={educationCoursePath}
        />
        <CourseLegalDisclaimer subject="This challenge" />
      </div>
    );
  }

  const renderPage = (content) => (
    <div
      className={`course-page challenge-page${controlRoom ? ' challenge-page--workbench' : ''}${isPostPurchase ? ' challenge-page--post-purchase' : ''}${trimmed ? ' challenge-page--trimmed' : ''}`}
      data-testid="challenge-page"
      data-challenge-layout={trimmed ? 'trimmed' : undefined}
      id="challenge-top"
      role="main"
      tabIndex="-1"
    >
      <ScrollProgress />
      <PrerenderReady ready={prerenderReady} state={prerenderState} />
      {pageHead}

      {isPostPurchase ? (
        <section className="chp-post-purchase" data-testid="post-purchase-challenge-context">
          <div className="chp-post-purchase__inner">
            <p className="chp-post-purchase__status">BotSpot plan active</p>
            <p className="chp-post-purchase__next">Join the free {challengeName}</p>
            <Link className="chp-post-purchase__skip" to={postPurchaseDestination}>
              Skip to BotSpot
            </Link>
          </div>
        </section>
      ) : null}

      {content}
      {presentation === 'clarity-home' ? (
        <details className="challenge-home__legal">
          <summary>
            <span>Educational only. Trading involves risk.</span>
            <span>Full disclaimer</span>
          </summary>
          <CourseLegalDisclaimer subject="This challenge" />
        </details>
      ) : (
        <CourseLegalDisclaimer subject="This challenge" />
      )}
    </div>
  );

  if (presentation === 'clarity-home' && !registered) {
    return renderPage(
      <ChallengePageClarityHome
        challengeName={challengeName}
        description={pageDescription}
        steps={BOARD_STEPS}
        questions={FAQS}
        registration={registrationPanel}
        kickoffLabel={kickoffLabel}
        isLoading={isLoading}
        registrationOpen={registrationOpen}
        focusRegistrationForm={focusRegistrationForm}
      />,
    );
  }

  return renderPage(
    <>
      <header
        className={
          controlRoom
            ? `chx-hero${isPostPurchase ? ' chx-hero--post-purchase' : ''}`
            : `chp-hero-grid${isPostPurchase ? ' chp-hero-grid--post-purchase' : ''}`
        }
      >
        <div className={controlRoom ? 'chx-hero__story' : 'chp-hero-copy'}>
          {controlRoom ? (
            <>
              <Reveal instant className="chx-hero__headline-reveal" delay={50}>
                <h1 className="chx-hero__title">
                  {isClarity ? (
                    <span className="chx-hero__line">{challengeName}</span>
                  ) : (
                    <>
                      {workbenchCopy.titleLines.map((line) => (
                        <span className="chx-hero__line" key={line}>
                          {line}
                        </span>
                      ))}
                      <span className="chx-hero__line">
                        to <span className="chx-hero__evidence">{workbenchCopy.titleEmphasis}</span>
                      </span>
                    </>
                  )}
                </h1>
              </Reveal>
              <Reveal instant className="chx-hero__sub-reveal" delay={110}>
                <p className="chx-hero__sub">{workbenchCopy.sub}</p>
              </Reveal>
              {isClarity ? (
                <a
                  className="cp-btn-primary chx-hero__registration-jump"
                  href={registered ? '#challenge-confirmation' : '#challenge-register'}
                  data-testid="challenge-registration-jump"
                >
                  {registered
                    ? 'View your session links'
                    : loadError
                      ? 'Retry the schedule'
                      : registrationClosed
                        ? 'Ask about next dates'
                        : 'Register free'}
                </a>
              ) : null}
              <Reveal instant className="chx-hero__briefing-reveal" delay={150}>
                <section
                  className="chx-hero__briefing"
                  data-testid="challenge-hero-briefing"
                  aria-label="Challenge overview"
                >
                  <div className="chx-hero__host">
                    <a
                      className="chx-hero__host-link"
                      href={ROB_LINKEDIN_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="View Rob Grzesik on LinkedIn"
                    >
                      <span className="chx-hero__host-photo">
                        <img src="/images/rob.jpg" alt="" width="52" height="52" decoding="async" />
                      </span>
                      <strong>
                        {workbenchCopy.hostPrefix}
                        <span className="chx-hero__host-name">Rob Grzesik</span>
                      </strong>
                    </a>
                    <span className="chx-hero__host-creds">{workbenchCopy.hostCreds}</span>
                  </div>
                  <p className="chx-hero__format">{workbenchCopy.format}</p>
                  <div className="chx-hero__sessions" data-testid="challenge-hero-schedule">
                    <article>
                      <h2>Kickoff with Rob</h2>
                      <strong>
                        {kickoffLabel || (isLoading ? 'Loading date' : 'Next date coming')}
                      </strong>
                      {workbenchCopy.kickoffSpan ? <span>{workbenchCopy.kickoffSpan}</span> : null}
                    </article>
                  </div>
                </section>
              </Reveal>
            </>
          ) : trimmed ? (
            <>
              {/* Arms C and D (Rob, 2026-10-20): one badge, the headline, one
                  line of subheading, one date row with the countdown inline,
                  and one compact host row, so the first form field shows on
                  the first phone screen. Dates come from the catalog rows. */}
              <Reveal instant>
                <p className="chp-live-badge" data-testid="challenge-live-badge">
                  <span className="chp-live-dot" aria-hidden="true" />
                  {trimmedCopy.badge}
                </p>
              </Reveal>
              <Reveal instant delay={50}>
                <h1 className="chp-hero-title">{trimmedCopy.heading || challengeName}</h1>
              </Reveal>
              <Reveal instant delay={90}>
                <p className="chp-hero-sub" data-testid="challenge-hero-sub">
                  {trimmedCopy.subheading || pageDescription}
                </p>
              </Reveal>
              <Reveal instant delay={120}>
                <div className="chp-when-row" data-testid="challenge-when">
                  <span className="chp-when-row__date">
                    {kickoffLabel ||
                      (isLoading
                        ? 'Loading dates from catalog...'
                        : 'Dates unavailable from catalog')}
                  </span>
                  {kickoffAt ? (
                    <PromoCountdown
                      endsAt={kickoffAt}
                      label={CHALLENGE_TRIMMED_COUNTDOWN_LABEL}
                      variant="clock"
                    />
                  ) : null}
                </div>
              </Reveal>
              <Reveal instant delay={140}>
                <div className="chp-host-row" data-testid="challenge-host-row">
                  <a
                    className="chp-host-row__photo"
                    href={ROB_LINKEDIN_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="View Rob Grzesik on LinkedIn"
                  >
                    <img src="/images/rob.jpg" alt="Rob Grzesik" width="40" height="40" />
                  </a>
                  <p className="chp-host-row__text">
                    <a href={ROB_LINKEDIN_URL} target="_blank" rel="noopener noreferrer">
                      Rob Grzesik
                    </a>
                    {` · ${CHALLENGE_TRIMMED_HOST_CREDENTIALS}`}
                  </p>
                </div>
              </Reveal>
            </>
          ) : (
            <>
              <Reveal instant>
                <p className="chp-live-badge">
                  <span className="chp-live-dot" aria-hidden="true" />
                  Free · Seven days · Live with Rob
                </p>
              </Reveal>
              <Reveal instant delay={50}>
                <h1 className="chp-hero-title">{challengeName}</h1>
              </Reveal>
              <Reveal instant delay={90}>
                <p className="chp-hero-sub">{pageDescription}</p>
              </Reveal>
              <Reveal instant delay={120}>
                <p className="chp-when-line" data-testid="challenge-when">
                  {kickoffLabel
                    ? `Kickoff ${kickoffLabel}`
                    : isLoading
                      ? 'Loading dates from catalog...'
                      : 'Dates unavailable from catalog'}
                </p>
              </Reveal>
              {isPostPurchase ? null : (
                <Reveal instant delay={140}>
                  <div className="chp-host-inline">
                    <a
                      className="chp-host-photo-link"
                      href={ROB_LINKEDIN_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="View Rob Grzesik on LinkedIn"
                    >
                      <img src="/images/rob.jpg" alt="Rob Grzesik" width="52" height="52" />
                    </a>
                    <div>
                      <strong>
                        <a href={ROB_LINKEDIN_URL} target="_blank" rel="noopener noreferrer">
                          Rob Grzesik
                        </a>
                      </strong>
                      <span>
                        Managed $3B+ in transactions at Greystone · early Voyager Digital engineer
                        (company peaked near $1B) · 15+ years in financial technology
                      </span>
                    </div>
                  </div>
                </Reveal>
              )}
              {kickoffAt ? (
                <Reveal instant delay={160}>
                  <PromoCountdown endsAt={kickoffAt} label="Kickoff starts in" variant="blocks" />
                </Reveal>
              ) : null}
            </>
          )}
        </div>

        <div className={controlRoom ? 'chx-hero__desk' : 'chp-hero-form'}>{registrationPanel}</div>
      </header>

      {controlRoom && !registered ? (
        <>
          <section
            className="chx-brief"
            data-testid="challenge-board"
            id="challenge-week"
            aria-labelledby="challenge-brief-heading"
          >
            <header className="chx-brief__heading chx-motion-heading" data-chx-reveal="pending">
              <h2 id="challenge-brief-heading">{workbenchCopy.briefHeading}</h2>
              <p>{isClarity ? pageDescription : workbenchCopy.briefLede}</p>
            </header>

            <ol className="chx-brief__decisions" data-testid="challenge-outcome">
              {workbenchOutcomes.map((outcome, index) => (
                <li className="chx-motion-decision" data-chx-reveal="pending" key={outcome.stage}>
                  <span className="chx-brief__number" aria-hidden="true">
                    0{index + 1}
                  </span>
                  <div className="chx-brief__decision-copy">
                    <h3>
                      {isClarity ? outcome.question : `${outcome.stage}: ${outcome.question}`}
                    </h3>
                    <p>{outcome.detail}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <div className="chx-brief__gate chx-motion-gate" data-chx-reveal="pending">
            <strong>{workbenchCopy.gateLabel}</strong>
            <p>{workbenchCopy.gate}</p>
          </div>

          <section
            className="chx-brief__live chx-motion-live"
            data-chx-reveal="pending"
            data-testid="challenge-when"
          >
            <header>
              <h2>{workbenchCopy.liveHeading}</h2>
            </header>
            <div className="chx-brief__live-track">
              <article>
                <h3>Kickoff with Rob</h3>
                <strong>
                  {kickoffLabel || (isLoading ? 'Loading next date' : 'Next date coming')}
                </strong>
                <p>{workbenchCopy.kickoffBody}</p>
              </article>
              <div className="chx-brief__duration" aria-hidden="true">
                <strong>7</strong>
                <span>days</span>
              </div>
            </div>
          </section>

          <aside
            className="chx-brief__practical chx-motion-practical"
            data-chx-reveal="pending"
            data-testid="challenge-includes"
          >
            <div>
              <h3>Included</h3>
              <ul>
                {workbenchLogistics.map((item) => (
                  <li key={item.name}>
                    <strong>{item.name}</strong>
                    <span>{item.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3>Bring</h3>
              <ul>
                {workbenchRequirements.map((item) => (
                  <li key={item.name}>
                    <strong>{item.name}</strong>
                    <span>{item.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </>
      ) : null}

      {controlRoom ? null : (
        <section className="chp-showcase" aria-label="What you will build">
          <Reveal>
            <img
              className="chp-showcase-art"
              src="/images/challenges/challenge-hero-dashboard.webp"
              alt="AI trading workflow with a watchlist, portfolio view, thesis, and sourced market updates"
              width="1376"
              height="768"
              loading="eager"
            />
            <p className="chp-showcase-caption">
              The project for the week: one AI trading workflow that researches the market, makes
              risk visible, and prepares a trade for your review.
            </p>
          </Reveal>
        </section>
      )}

      {controlRoom ? null : (
        <section
          className="chp-details"
          aria-label="Challenge details"
          data-testid="challenge-details"
        >
          <Reveal>
            <ul>
              {details.map((item) => (
                <li key={item.label}>
                  <span className="chp-details-label">{item.label}</span>
                  <span className="chp-details-value">{item.value}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </section>
      )}

      {controlRoom ? null : (
        <section className="cp-section" data-testid="challenge-board" id="challenge-week">
          <Reveal>
            <h2>How the week works</h2>
          </Reveal>
          <Reveal delay={50}>
            <p className="cp-sub">
              One live session around one project. Set it up together, then keep improving it during
              the week.
            </p>
          </Reveal>
          <ol className="chp-board-steps">
            {BOARD_STEPS.map((step, i) => (
              <Reveal as="li" key={step.title} delay={i * 70}>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </Reveal>
            ))}
          </ol>
          <div className="chp-mid-cta">
            <a href="#challenge-register" onClick={focusRegistrationForm}>
              Save my free seat
            </a>
          </div>
        </section>
      )}

      {controlRoom && !registered ? (
        <>
          <section
            className="chx-mentor chx-motion-mentor"
            data-chx-reveal="pending"
            data-testid="challenge-instructor"
            aria-labelledby="challenge-instructor-heading"
          >
            <figure className="chx-mentor__portrait">
              <a
                href={ROB_LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View Rob Grzesik on LinkedIn"
              >
                <img src="/images/rob.jpg" alt="Rob Grzesik" width="640" height="640" />
              </a>
            </figure>
            <div className="chx-mentor__story">
              <div className="chx-mentor__bio">
                <h2 id="challenge-instructor-heading">{workbenchCopy.mentorHeading}</h2>
                <p>{workbenchCopy.mentorBio1}</p>
                {workbenchCopy.mentorBio2 ? <p>{workbenchCopy.mentorBio2}</p> : null}
                <a
                  className={
                    isClarity ? 'chx-mentor__link chx-mentor__link--icon' : 'chx-mentor__link'
                  }
                  href={ROB_LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={isClarity ? 'LinkedIn' : undefined}
                >
                  {isClarity ? (
                    <FontAwesomeIcon icon={faLinkedin} aria-hidden="true" />
                  ) : (
                    <>
                      See Rob's experience
                      <span aria-hidden="true">↗</span>
                    </>
                  )}
                </a>
              </div>
              {workbenchCopy.showProof ? (
                <dl className="chx-mentor__proof">
                  <div>
                    <dt>$3B+</dt>
                    <dd>transactions processed</dd>
                  </div>
                  <div>
                    <dt>1,000+</dt>
                    <dd>trading systems built</dd>
                  </div>
                  <div>
                    <dt>14,000+</dt>
                    <dd>students taught</dd>
                  </div>
                </dl>
              ) : null}
            </div>
          </section>
        </>
      ) : null}

      {registered ? (
        <>
          <section className="cp-section" data-testid="challenge-schedule">
            <Reveal>
              <h2>Your live schedule</h2>
            </Reveal>
            <ul className="chp-session-list">
              {sessions.map((session) => {
                const calendarLinks = buildChallengeCalendarLinks(
                  session,
                  getApiBaseUrl() || 'https://api.botspot.trade',
                );
                return (
                  <li key={session.id} data-testid={`challenge-session-${session.id}`}>
                    <div>
                      <strong>{canonicalChallengeSessionTitle(session.title)}</strong>
                      <span>{formatChallengeDateTime(session.scheduledAt)}</span>
                    </div>
                    <div className="chp-session-actions">
                      {session.joinUrl ? (
                        <a href={session.joinUrl} target="_blank" rel="noopener noreferrer">
                          Join on Zoom
                        </a>
                      ) : null}
                      {youtubeLiveUrl(session) ? (
                        <a
                          href={youtubeLiveUrl(session)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`challenge-youtube-live-${session.id}`}
                        >
                          Get notified on YouTube
                        </a>
                      ) : null}
                      {calendarLinks ? (
                        <>
                          <a
                            href={calendarLinks.google}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`challenge-google-calendar-${session.id}`}
                          >
                            Google Calendar
                          </a>
                          <a
                            href={calendarLinks.ics}
                            download={`${canonicalChallengeSessionTitle(session.title)}.ics`}
                            data-testid={`challenge-ics-${session.id}`}
                          >
                            Apple / Outlook
                          </a>
                        </>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="cp-section" data-testid="challenge-includes">
            <Reveal>
              <h2>Support during the week</h2>
            </Reveal>
            <ul className="chp-includes">
              {CHALLENGE_LOGISTICS.map((item, i) => (
                <Reveal as="li" key={item.name} delay={i * 40}>
                  <strong>{item.name}</strong>
                  {' · '}
                  {item.detail}
                </Reveal>
              ))}
            </ul>
          </section>

          <section
            className="cp-section"
            data-testid="challenge-submission"
            id="challenge-submission"
          >
            <Reveal>
              <h2>Share your project (optional)</h2>
            </Reveal>
            <Reveal delay={50}>
              <p className="cp-sub">
                There is no submission requirement. Share a screenshot or public link only if you
                want feedback on the live show.
              </p>
            </Reveal>
            {isAuthenticated ? (
              <form className="chp-submission-form" onSubmit={saveSubmission}>
                <label>
                  What did you build?
                  <textarea
                    required
                    value={submission.description}
                    onChange={(event) =>
                      setSubmission({
                        ...submission,
                        description: event.target.value,
                      })
                    }
                  />
                </label>
                <label>
                  Public link (optional if you upload a screenshot)
                  <input
                    type="url"
                    value={submission.publicLink}
                    onChange={(event) =>
                      setSubmission({
                        ...submission,
                        publicLink: event.target.value,
                      })
                    }
                  />
                </label>
                <label>
                  Screenshot (optional if you add a public link)
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(event) =>
                      setSubmission({
                        ...submission,
                        screenshotFile: event.target.files?.[0] || null,
                      })
                    }
                  />
                </label>
                <label>
                  Question for Rob (optional)
                  <input
                    type="text"
                    value={submission.question}
                    onChange={(event) =>
                      setSubmission({
                        ...submission,
                        question: event.target.value,
                      })
                    }
                  />
                </label>
                <label className="cp-check">
                  <input
                    type="checkbox"
                    checked={submission.showcasePermission}
                    onChange={(event) =>
                      setSubmission({
                        ...submission,
                        showcasePermission: event.target.checked,
                      })
                    }
                  />
                  <span>BotSpot may show this project on the live show.</span>
                </label>
                <button type="submit" className="cp-btn-primary" disabled={submissionSaving}>
                  {submissionSaving ? 'Saving...' : 'Save my project'}
                </button>
                {submissionSaved ? <p role="status">Project saved.</p> : null}
                {submissionError ? <p role="alert">{submissionError}</p> : null}
              </form>
            ) : (
              <div className="chp-submission-signin">
                <p>Sign in with the same email you registered with to share your project.</p>
                {isClarity ? (
                  <Link
                    className="cp-btn-primary"
                    state={buildAuthOverlayRouteState(location)}
                    to={`/login?returnTo=${encodeURIComponent(`${location.pathname}${location.search}#challenge-submission`)}&source=challenge_submission`}
                  >
                    Sign in to share
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="cp-btn-primary"
                    onClick={() =>
                      loginWithRedirect({
                        appState: {
                          returnTo: `${window.location.pathname}${window.location.search}#challenge-submission`,
                        },
                      })
                    }
                  >
                    Sign in to share
                  </button>
                )}
              </div>
            )}
          </section>

          {availableReplays.length ? (
            <section className="cp-section">
              <Reveal>
                <h2>Replays</h2>
              </Reveal>
              <ul className="cp-archive">
                {availableReplays.map((s) => (
                  <Reveal as="li" key={s.id}>
                    <strong>{canonicalChallengeSessionTitle(s.title)}</strong>
                    <br />
                    <a href={s.recordingUrl} target="_blank" rel="noopener noreferrer">
                      Watch the recording
                    </a>
                  </Reveal>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      ) : (
        <section className="cp-section cp-faq" data-testid="challenge-faq" id="challenge-questions">
          <Reveal>
            <h2>Questions</h2>
          </Reveal>
          {controlRoom ? (
            <div className="chx-faq__reveal" data-chx-reveal="pending">
              <div className="chp-faq-list">
                {CONTROL_ROOM_FAQS.map((item) => (
                  <details key={item.q}>
                    <summary>
                      <span>{item.q}</span>
                      <ChevronDown aria-hidden="true" size={20} strokeWidth={2} />
                    </summary>
                    <p>{item.a}</p>
                  </details>
                ))}
              </div>
            </div>
          ) : (
            <dl>
              {FAQS.map((item, i) => (
                <Reveal as="div" key={item.q} delay={i * 35}>
                  <dt>{item.q}</dt>
                  <dd>{item.a}</dd>
                </Reveal>
              ))}
            </dl>
          )}
        </section>
      )}

      {!registered && !controlRoom ? (
        <section className="cp-section" data-testid="challenge-final-cta">
          {/* No banner art here: the second form is the whole point of the
              section and decoration above it pushed it below the fold on
              mobile. */}
          <div className="chp-final">
            <div className="chp-final-inner">
              <Reveal>
                <h2>
                  {controlRoom
                    ? 'Build it this week. Keep using it next week.'
                    : 'One week from now, your AI trading workflow is ready to use'}
                </h2>
                <p>
                  {kickoffLabel
                    ? `Kickoff ${formatChallengeDate(kickoffAt)}. Free. No credit card.`
                    : 'Join the free live challenge when dates are available.'}
                </p>
              </Reveal>
              <Reveal delay={80}>
                <RegistrationForm
                  form={form}
                  setForm={setForm}
                  onSubmit={register}
                  submitting={submitting || awaitingCatalogForHeldPress}
                  error={error}
                  phoneError={phoneError}
                  clearPhoneError={clearPhoneError}
                  disabled={registrationClosed}
                  disabledReason={loadError}
                  ctaLabel="I'm in. Save my free seat"
                  testId="challenge-form-final"
                  questions={questionsFor('challenge-form-final')}
                  step={qualifyingQuestion}
                />
              </Reveal>
              <p className="chp-secondary">
                Want the full five-week course after the challenge?{' '}
                <Link to={educationCoursePath}>See the AI Trading Bootcamp</Link>
              </p>
            </div>
          </div>
        </section>
      ) : !registered && controlRoom ? (
        <section
          className="crx-closing chx-closing"
          data-chx-reveal="pending"
          aria-labelledby="crx-closing-heading"
          data-testid="challenge-closing"
        >
          <div className="chx-closing__statement">
            <h2 id="crx-closing-heading">
              {registrationClosed
                ? workbenchCopy.closingWithoutDate
                : workbenchCopy.closingWithDate}
            </h2>
          </div>
          <div className="chx-closing__response">
            <p className="crx-closing-lede">
              {registrationClosed
                ? registrationStatus.body
                : kickoffAt
                  ? `Kickoff ${formatChallengeDate(kickoffAt)}. Free. No credit card.`
                  : 'Free. No credit card.'}
            </p>
            <div className="chx-closing__actions">
              {registrationClosed ? (
                <a className="cp-btn-primary" href={NEXT_CHALLENGE_CONTACT_URL}>
                  Ask for next dates
                </a>
              ) : (
                <a
                  className="cp-btn-primary"
                  href="#challenge-register"
                  onClick={focusRegistrationForm}
                >
                  {workbenchCopy.ctaLabel}
                </a>
              )}
              <Link to={educationCoursePath}>Compare the five-week course</Link>
            </div>
          </div>
        </section>
      ) : registered ? (
        <section className="cp-section cp-final">
          <Reveal>
            <h2>You are locked in for {runTitle}</h2>
            <p className="cp-muted">Watch your inbox for tips after kickoff.</p>
            {isPostPurchase ? (
              <Link
                className="cp-btn-primary"
                to={postPurchaseDestination}
                data-testid="post-purchase-challenge-continue"
              >
                Continue to BotSpot
              </Link>
            ) : null}
          </Reveal>
        </section>
      ) : null}
    </>,
  );
};

export default ChallengePage;
